import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { FormikHelpers } from "formik";
import { useSnackbar } from "notistack";

import { INotificationCreateDTO } from "@modules/notifications/model/INotifications";

import { useUserState } from "@context/UserContext";
import ROUTES from "@config/routes";

import {
  INotificationFormValues,
  formInitialValues,
} from "../schema/notifications.schema";
import { useNotificationContext } from "../../../context";

import { INotificationFormMode, CREATE, EDIT, VIEW } from "./interface";
import { readFileAsDataUrl } from "./helper";

import { invalidType, preview404, read404, sizeMax } from "./message";

export const useNotificationForm = (mode: INotificationFormMode) => {
  const isCreate = mode === CREATE;
  const isEdit = mode === EDIT;
  const isView = mode === VIEW;

  const { id } = useParams<{ id: string }>();
  const { actions } = useNotificationContext();

  const { enqueueSnackbar } = useSnackbar();
  const userState = useUserState();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState<INotificationFormValues>();

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | undefined>();
  const [previewOpen, setPreviewOpen] = useState(false);

  const buildPayload = async (
    values: INotificationFormValues
  ): Promise<INotificationCreateDTO> => {
    let finalImageUrl: string | undefined;

    // NOTE: Reaproveita imageUrl já persistida, desde que NÃO seja um blob antigo
    if (values.imageUrl && !values.imageUrl.startsWith("blob:")) {
      finalImageUrl = values.imageUrl;
    }

    // NOTE:  Se o usuário selecionou um novo arquivo, sobrescreve com dataURL
    if (imageFile) {
      try {
        finalImageUrl = await readFileAsDataUrl(imageFile);
      } catch (err) {
        enqueueSnackbar(read404, { variant: "error" });
      }
    }
    return {
      title: values.title,
      message: values.message,
      targets: {
        centers: values.centers,
        segments: values.segments,
        profiles: values.profiles,
      },
      startAt: new Date(values.startAt).toISOString(),
      endAt: new Date(values.endAt).toISOString(),
      imageUrl: finalImageUrl,
    };
  };

  const handleSubmit = async (
    values: INotificationFormValues,
    helpers: FormikHelpers<INotificationFormValues>
  ) => {
    console.log(values);
    if (isView) return;
    setLoading(true);
    try {
      const payload = await buildPayload(values);

      isEdit && id && (await actions.edit(id, payload));

      isCreate && (await actions.create(payload, userState.username));

      handleRedirectAfterSubmit(values.startAt);
    } catch (error) {
      helpers.setSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRedirectAfterSubmit = (date: string) => {
    const now = new Date();
    const start = new Date(date);

    const tabParam = start > now ? "programadas" : "ativas";

    // NOTE: Redireciona para a lista já com a aba correta
    history.push(`${ROUTES.ADMIN_ROUTES.NOTIFICATIONS.ROOT}?tab=${tabParam}`);
  };

  const handleRemoveFile = () => {
    setImageFile(null);
    setImagePreviewUrl(undefined);
  };

  const handlePreview = async (values: INotificationFormValues) => {
    // Se ainda não temos preview mas temos arquivo, gera dataURL on-demand
    if (!imagePreviewUrl && imageFile) {
      try {
        const dataUrl = await readFileAsDataUrl(imageFile);
        setImagePreviewUrl(dataUrl);
      } catch {
        enqueueSnackbar(preview404, { variant: "error" });
        return;
      }
    }

    setPreviewOpen(true);
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowed = ["image/png", "image/jpeg", "image/webp"];
    if (!allowed.includes(file.type)) {
      enqueueSnackbar(invalidType, { variant: "error" });
      event.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      enqueueSnackbar(sizeMax, { variant: "error" });
      event.target.value = "";
      return;
    }

    setImageFile(file);

    // NOTE: Gera dataURL somente para PREVIEW visual
    readFileAsDataUrl(file)
      .then((dataUrl) => {
        setImagePreviewUrl(dataUrl);
      })
      .catch(() => {
        enqueueSnackbar(read404, { variant: "error" });
      });
  };

  const handleCancelOrClose = () => {
    history.push(ROUTES.ADMIN_ROUTES.NOTIFICATIONS.ROOT);
  };

  useEffect(() => {
    if (isCreate) {
      return setInitialValues(formInitialValues);
    }

    if ((isEdit || isView) && id) {
      const load = async () => {
        const notificationFind = await actions.readById(Number(id));
        if (notificationFind) {
          const sanitizedImageUrl =
            notificationFind.imageUrl &&
            !notificationFind.imageUrl.startsWith("blob:")
              ? notificationFind.imageUrl
              : undefined;
          setInitialValues({
            title: notificationFind.title,
            message: notificationFind.message,
            centers: notificationFind.targets.centers,
            segments: notificationFind.targets.segments,
            profiles: notificationFind.targets.profiles,
            startAt: notificationFind.startAt,
            endAt: notificationFind.endAt,
            imageUrl: sanitizedImageUrl,
          });
          setImagePreviewUrl(notificationFind.imageUrl);
        }
      };
      void load();
    }
  }, [isCreate, isEdit, isView, actions, id]);

  return {
    state: {
      loading,
      initialValues,
      previewOpen,
      isCreate,
      isEdit,
      isView,
      imageFileName:
        imageFile?.name || (imagePreviewUrl ? "notificação.jpg" : ""),
      imagePreviewUrl,
      mode,
    },
    actions: {
      handleCancelOrClose,
      handleSubmit,
      handlePreview,
      handleRemoveFile,
      handleUpload,
      closePreview: () => setPreviewOpen(false),
    },
  };
};
